<template>
  <v-container fluid class="creditors-manager pa-0">
    <v-layout
      v-if="$checkRole('creditor_admin')"
      column
      class="creditors-manager__layout pa-0"
    >
      <v-flex shrink row class="creditors-manager__header pt-0 pb-2 px-5">
        <v-layout row align-center justify-space-between>
          <v-layout row align-center>
            <div class="headline">
              {{ pageTitle }}
            </div>

            <v-tooltip bottom v-if="creditorId">
              <v-btn
                slot="activator"
                icon
                class="ml-2"
                :disabled="isLoading"
                @click="viewHistory"
              >
                <v-icon color="primary">history</v-icon>
              </v-btn>
              <span>ver historico de alterações</span>
            </v-tooltip>
          </v-layout>

          <v-layout v-if="creditorId" column align-end>
            <v-layout v-if="form.created_at" row align-center class="mb-1">
              <p class="mb-0 grey--text">
                Criado
                {{ form.created_at | formatDateAgo }}, em
                {{ form.created_at | formatDateTime }}
              </p>
            </v-layout>

            <v-layout v-if="form.last_prepared_at" row align-center>
              <p class="mb-0 grey--text">
                Último preparo
                {{ form.last_prepared_at | formatDateAgo }}, em
                {{ form.last_prepared_at | formatDateTime }}
              </p>
            </v-layout>
          </v-layout>
        </v-layout>
      </v-flex>

      <v-divider />

      <v-flex class="creditors-manager__body py-0 pl-5 pr-0">
        <div class="creditors-manager__body-scroll pt-2 pr-5 pb-4">
          <v-layout row wrap>
            <v-flex xs12>
              <v-layout flex-row>
                <v-flex xs12 md1>
                  <v-text-field
                    v-model.number="form.creditor_id"
                    type="number"
                    label="ID do credor"
                    class="ml-2"
                    box
                    hide-details
                    :disabled="isLoading || isEditMode"
                    :rules="[
                      (v) => !!v || 'Campo obrigatório',
                      (v) => v > 0 || 'Número não permitido',
                    ]"
                  />
                </v-flex>

                <v-flex xs12 md3>
                  <v-text-field
                    v-model="form.name"
                    label="Nome"
                    class="ml-2 mb-1"
                    clearable
                    box
                    :disabled="isLoading"
                    :rules="[(v) => !!v || 'Campo obrigatório']"
                  />
                </v-flex>

                <v-flex xs12 md4 ml-2>
                  <v-text-field
                    v-model="form.adapter"
                    label="Adapter"
                    class="ml-2 mb-1"
                    clearable
                    box
                    :disabled="isLoading"
                    :rules="[(v) => !!v || 'Campo obrigatório']"
                  />
                </v-flex>
              </v-layout>

              <v-layout flex-row>
                <v-switch
                  v-model="form.active"
                  label="Ativo"
                  class="ml-2 mb-1 mt-0 pt-0"
                  color="primary"
                  box
                  :disabled="isLoading"
                  style="max-width: fit-content"
                />

                <v-switch
                  :value="form.skip_multicreditor === 1"
                  @change="form.skip_multicreditor = $event ? 1 : 0"
                  label="Pular o contato (Skip multicreditor)"
                  class="ml-5 mb-1 mt-0 pt-0"
                  color="primary"
                  box
                  :disabled="isLoading"
                  persistent-hint
                  hint="Indica se deve pular o contato se já foi notificado por outro credor"
                />
              </v-layout>
            </v-flex>

            <edit-topic title="Consolidação" hide-button>
              <v-layout flex-row>
                <v-flex xs12 md4>
                  <v-text-field
                    v-model="form.cons_schema"
                    label="Consolidação (schema)"
                    clearable
                    box
                    class="ml-2"
                    :disabled="isLoading"
                  />
                </v-flex>

                <v-flex xs12 md4 ml-2>
                  <v-select
                    v-model="form.import_priority"
                    :items="importPriorityOptions"
                    label="Prioridade de importacao na fila import-debts"
                    box
                    clearable
                    persistent-hint
                    :hint="lastImportHint"
                    :disabled="isLoading"
                  />
                </v-flex>
              </v-layout>

              <v-layout flex-row>
                <v-switch
                  v-model="form.auto_consolidation"
                  style="max-width: fit-content"
                  label="Consolidação automática"
                  class="ml-2 mb-1 mt-0"
                  color="primary"
                  box
                  :disabled="isLoading"
                />
                <v-switch
                  v-model="form.allow_express"
                  label="Permitir express"
                  class="ml-5 mb-1 mt-0"
                  color="primary"
                  box
                  :disabled="isLoading"
                />
              </v-layout>
            </edit-topic>

            <edit-topic
              title="Iscas do disparo"
              :tooltip="`<span>
                  Dados referente as iscas do disparo<br />
                  Exemplo: <br />{ <br />&nbsp;emails:['quiteja@quiteja.com'],
                  <br />&nbsp;sms:['90990909990']<br />
                  }
                </span>`"
              button-text="editar iscas"
              @btn-click="openBaitDataDialog"
            >
              <small v-if="form.bait_data_text">
                <pre>{{ form.bait_data_text }}</pre>
              </small>
              <v-layout
                v-else
                row
                align-center
                class="mt-2 mb-1 grey--text text--darken-1"
              >
                <v-icon small class="mr-1">info</v-icon>
                <span>Nenhum dado de iscas.</span>
              </v-layout>
            </edit-topic>

            <edit-topic
              title="Variáveis"
              tooltip="Variáveis permitidas por credor"
              button-text="editar variáveis"
              button-icon="code-brackets"
              @btn-click="openAllowedVariablesDialog"
            >
              <v-layout row wrap>
                <v-flex xs12>
                  <v-chip
                    v-for="(variable, index) in form.allowed_variables"
                    :key="index"
                    small
                  >
                    <span>{{ variable }}</span>
                    <v-icon
                      small
                      class="ml-2 grey--text"
                      @click="removeVariable(index)"
                    >
                      delete
                    </v-icon>
                  </v-chip>
                </v-flex>

                <v-flex xs3>
                  <v-text-field
                    v-model="newVariable"
                    label="Adicionar nova variável"
                    hint="Pressione Enter para adicionar"
                    persistent-hint
                    class="mt-3"
                    box
                    @input="formatNewVariable"
                    @keyup.enter="addVariable"
                  />
                </v-flex>
              </v-layout>
            </edit-topic>

            <edit-topic
              title="Skips (Redis locks)"
              button-text="editar skips"
              @btn-click="openRedisLocksDialog"
            >
              <v-layout v-if="parsedRedisLocks" column>
                <v-layout
                  v-for="(config, key) in parsedRedisLocks"
                  :key="key"
                  row
                  align-center
                  class="mb-0"
                >
                  <v-flex xs2>
                    <span class="tag-approach-type" :class="key">
                      {{ key }}
                    </span>
                  </v-flex>

                  <v-flex xs2>
                    <v-text-field
                      :value="config.lock_hours"
                      type="number"
                      label="Horas"
                      append-icon="schedule"
                      class="ml-2"
                      dense
                      hide-details
                      @input="updateLockHours(key, $event)"
                      :disabled="isLoading"
                    />
                  </v-flex>
                </v-layout>
              </v-layout>

              <v-layout v-else row align-center class="error--text">
                <span class="ml-2">JSON de Redis locks inválido</span>
              </v-layout>
            </edit-topic>

            <edit-topic
              title="Grupos de estimulo"
              tooltip="Organização ECS, disparos automáticos de hora em hora do airflow"
              button-text="editar grupos"
              @btn-click="openStimulusGroupDialog"
            >
              <v-layout v-if="hasStimulusGroups" row wrap class="mt-2 mb-1">
                <v-flex
                  v-for="(group, groupIndex) in stimulusGroupsDisplay"
                  :key="`stimulus-group-${groupIndex}`"
                  xs12
                  class="mb-1"
                >
                  <v-layout row wrap align-center>
                    <v-chip
                      color="grey darken-2"
                      text-color="white"
                      class="mr-2 mb-1"
                    >
                      Grupo {{ groupIndex + 1 }}
                    </v-chip>

                    <v-chip
                      v-for="(channel, channelIndex) in group"
                      :key="`stimulus-group-${groupIndex}-channel-${channelIndex}`"
                      small
                      outline
                      color="primary"
                      class="mr-1 mb-1"
                    >
                      {{ channel }}
                    </v-chip>
                  </v-layout>
                </v-flex>
              </v-layout>

              <v-layout
                v-else-if="isStimulusGroupJsonInvalid"
                row
                align-center
                class="mt-2 mb-1 error--text"
              >
                <v-icon small class="mr-1" color="error">error</v-icon>
                <span>JSON de grupos de estimulo inválido.</span>
              </v-layout>

              <v-layout
                v-else
                row
                align-center
                class="mt-2 mb-1 grey--text text--darken-1"
              >
                <v-icon small class="mr-1">info</v-icon>
                <span>Nenhum grupo de estimulo.</span>
              </v-layout>
            </edit-topic>

            <edit-topic title="Regras de quarentena Whatsapp" hide-button>
              <form-quarentine-rules
                :loading="isLoading"
                :rules="form.whatsapp_quarentine_rules"
                creditor-id="default"
                @update-rules="handleUpdateWhatsappQuarentineRules"
              />
            </edit-topic>
          </v-layout>
        </div>
      </v-flex>

      <v-divider />

      <v-flex
        v-if="madeChanges"
        shrink
        class="creditors-manager__footer pt-2 pb-1 px-5"
      >
        <v-layout row align-center justify-end>
          <v-btn flat @click="resetForm" :disabled="isLoading || !madeChanges">
            {{ isEditMode ? "Desfazer alterações" : "Limpar" }}
          </v-btn>
          <v-btn
            color="primary"
            dark
            @click="saveCreditor"
            :loading="isLoading"
            :disabled="!madeChanges"
          >
            {{ isEditMode ? "Salvar alterações" : "Criar Credor" }}
          </v-btn>
        </v-layout>
      </v-flex>

      <json-editor-dialog
        v-model="showJsonDialog"
        :title="jsonDialogTitle"
        :initial-json="jsonDialogInitialValue"
        @apply="onJsonApply"
        @cancel="onJsonCancel"
      />

      <dialog-creditor-history
        v-model="showCreditorHistory"
        :items="creditorHistoryItems"
        :loading="loadingCreditorHistory"
        :total-items="totalCreditorHistoryItems"
        @show-config-diff="openConfigDiff"
        @pagination-change="viewHistory"
      />

      <dialog-config-diff
        v-model="showConfigDiff"
        :current-config="selectedCurrentConfigDetails"
        :previous-config="selectedPreviousConfigDetails"
        :created-at="selectedHistoryCreatedAt"
        :user-name="selectedHistoryUserName"
        @close="handleConfigDiffClose"
      />
    </v-layout>
  </v-container>
</template>

<script>
import creditorService from "@/services/creditor";
import gatewaysService from "@/services/gateways";

import JsonEditorDialog from "./components/JsonEditorDialog";
import EditTopic from "./components/editTopic.vue";
import DialogCreditorHistory from "./components/dialogCreditorHistory.vue";
import DialogConfigDiff from "./components/DialogConfigDiff.vue";
import FormQuarentineRules from "@/views/consolidation/creditorsInfo/components/FormQuarentineRules.vue";

const REDIS_LOCKS_DEFAULT = {
  SMS: {
    lock_hours: 2,
  },
  PUSH: {
    lock_hours: 1,
  },
  CALL_PHONE: {
    lock_hours: 12,
  },
  CALL_CELLPHONE: {
    lock_hours: 12,
  },
};

const DEFAULT_WHATSAPP_QUARENTINE_RULES = {
  with_optin: {
    located: {
      1: 2,
      2: 2,
      3: 2,
      4: 2,
      5: 3,
    },
    not_located: {
      1: 2,
      2: 2,
      3: 2,
      4: 2,
      5: 7,
    },
  },
  without_optin: {
    located: {
      1: 2,
      2: 2,
      3: 3,
      4: 3,
      5: 7,
    },
    not_located: {
      1: 3,
      2: 7,
      3: 7,
      4: 7,
      5: 15,
    },
  },
};

function buildInitialForm() {
  return {
    name: "",
    creditor_id: null,
    active: true,
    auto_consolidation: false,
    import_priority: null,
    last_importation: null,
    last_prepared_at: null,
    bait_data_text: "",
    default_sms_gateway_id: null,
    default_email_gateway_id: null,
    default_call_gateway_id: null,
    sms_gateway_pool_id: null,
    allowed_variables: [],
    skip_multicreditor: null,
    desinterest_rate: 0,
    desinterest_min_limit_delay: null,
    redis_locks: REDIS_LOCKS_DEFAULT,
    redis_locks_text: JSON.stringify(REDIS_LOCKS_DEFAULT, null, 2),
    cons_schema: "consolidacao_carteira",
    allow_express: false,
    default_whatsapp_gateway_id: null,
    desinterest_max_limit_delay: null,
    desinterest_min_notification: 20,
    down_payment_rate: 0,
    next_day: "",
    stimulus_group: [["WHATSAPP", "SMS", "PUSH", "CALL_CELLPHONE", "EMAIL"]],
    stimulus_group_text: "",
    whatsapp_quarentine_rules: DEFAULT_WHATSAPP_QUARENTINE_RULES,
  };
}

export default {
  components: {
    JsonEditorDialog,
    EditTopic,
    DialogCreditorHistory,
    DialogConfigDiff,
    FormQuarentineRules,
  },
  props: {
    creditor_id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      form: buildInitialForm(),
      gateways: [],
      gatewayPools: [],
      showJsonDialog: false,
      jsonDialogTarget: "allowedVariables",
      creditorName: "",
      mainStateSnapshot: null,
      newVariable: "",
      numberRule: [
        (v) =>
          (v !== null && v !== undefined && v !== "") || "Campo obrigatório",
      ],
      //creditor history:
      showCreditorHistory: false,
      creditorHistoryItems: [],
      loadingCreditorHistory: false,
      totalCreditorHistoryItems: 0,
      showConfigDiff: false,
      selectedCurrentConfigDetails: {},
      selectedPreviousConfigDetails: {},
      selectedHistoryCreatedAt: "",
      selectedHistoryUserName: "",
    };
  },
  async created() {
    if (this.$checkRole("creditor_admin")) {
      await this.initializePage();
      this.buildMainStateSnapshot();
    } else {
      this.$router.push("/creditors-info");
    }
  },

  computed: {
    madeChanges() {
      if (!this.mainStateSnapshot) return false;
      return JSON.stringify(this.form) !== this.mainStateSnapshot;
    },
    creditorId() {
      if (!this.creditor_id) return null;
      const parsed = Number(this.creditor_id);
      if (Number.isNaN(parsed)) return null;
      return parsed;
    },
    isEditMode() {
      return this.creditorId !== null;
    },
    pageTitle() {
      return this.isEditMode
        ? `Editar credor #${this.creditorId} - ${this.creditorName}`
        : "Criar Novo Credor";
    },
    parsedRedisLocks() {
      try {
        return JSON.parse(this.form.redis_locks_text || "{}");
      } catch {
        return null;
      }
    },
    jsonDialogTitle() {
      if (this.jsonDialogTarget === "baitData") {
        return "Editar Iscas do disparo";
      }
      if (this.jsonDialogTarget === "redisLocks") {
        return "Editar Skips (Redis locks)";
      }
      if (this.jsonDialogTarget === "stimulusGroup") {
        return "Editar Grupos de estimulo";
      }
      return "Editar Variáveis permitidas";
    },
    jsonDialogInitialValue() {
      if (this.jsonDialogTarget === "baitData") {
        return this.form.bait_data_text || "{}";
      }
      if (this.jsonDialogTarget === "redisLocks") {
        return this.form.redis_locks_text || "{}";
      }
      if (this.jsonDialogTarget === "stimulusGroup") {
        return this.form.stimulus_group_text || "[]";
      }
      return this.form.allowed_variables;
    },
    lastImportHint() {
      if (!this.form.last_importation) {
        if (!this.creditorId) return "";
        return "Nenhuma importação ainda registrada";
      }
      return `Última importação ${this.$options.filters.formatDateAgo(
        this.form.last_importation,
      )}, em ${this.$options.filters.formatDateTime(
        this.form.last_importation,
      )}`;
    },
    parsedStimulusGroup() {
      const rawValue = (this.form.stimulus_group_text || "").trim();
      if (!rawValue) return [];
      try {
        const parsedValue = JSON.parse(rawValue);
        if (!Array.isArray(parsedValue)) return null;
        return parsedValue;
      } catch (error) {
        return null;
      }
    },
    stimulusGroupsDisplay() {
      if (!Array.isArray(this.parsedStimulusGroup)) return [];
      return this.parsedStimulusGroup
        .filter((group) => Array.isArray(group))
        .map((group) =>
          group
            .filter((channel) => typeof channel === "string" && channel.trim())
            .map((channel) => channel.trim()),
        )
        .filter((group) => group.length);
    },
    hasStimulusGroups() {
      return this.stimulusGroupsDisplay.length > 0;
    },
    isStimulusGroupJsonInvalid() {
      const hasText = (this.form.stimulus_group_text || "").trim().length > 0;
      if (!hasText) return false;
      return this.parsedStimulusGroup === null;
    },
    importPriorityOptions() {
      return Array.from({ length: 10 }, (_, i) => i + 1);
    },
  },
  methods: {
    async initializePage() {
      this.isLoading = true;
      try {
        // gateway options:
        const responsePools = await gatewaysService.list();
        this.gatewayPools = responsePools.objects || [];
        const responseGateways = await gatewaysService.gateways();
        this.gateways = responseGateways.objects || [];

        if (this.isEditMode) await this.loadCreditor();
      } catch (error) {
        this.snackbarError("Erro ao carregar dados do credor");
      } finally {
        this.isLoading = false;
      }
    },
    async loadCreditor() {
      if (!this.isEditMode) {
        this.snackbarError("Credor sem Id");
        return;
      }

      try {
        const response = await creditorService.list({
          creditor_id: this.creditorId,
          order: "id",
          // limit: 9999,
        });

        if (!response.objects || !response.objects.length) {
          this.snackbarError("Credor não encontrado");
          return;
        }
        const creditor = response.objects[0];

        this.creditorName = creditor.name || "";

        const locks = {}
        if(creditor.redis_locks) {
          for (const key of Object.keys(REDIS_LOCKS_DEFAULT)) {
            locks[key] = creditor.redis_locks[key] || {};
          }
        }

        this.form = {
          ...creditor,
          bait_data_text: this.stringifyJson(creditor.bait_data),
          redis_locks_text: this.stringifyJson(locks),
          stimulus_group_text: this.stringifyJson(creditor.stimulus_group),
          whatsapp_quarentine_rules: creditor.whatsapp_quarentine_rules || DEFAULT_WHATSAPP_QUARENTINE_RULES,
        };
      } catch (e) {
        this.snackbarError("Erro ao carregar credor");
      }
    },
    stringifyJson(value) {
      if (value === null || typeof value === "undefined") return "";
      try {
        return JSON.stringify(value, null, 2);
      } catch (error) {
        return "";
      }
    },
    parseJsonField(value, fieldName) {
      const trimmedValue = (value || "").trim();
      if (!trimmedValue) return null;
      try {
        return JSON.parse(trimmedValue);
      } catch (error) {
        this.snackbarError("Campo " + fieldName + " com JSON invalido");
        return null;
      }
    },
    openAllowedVariablesDialog() {
      this.jsonDialogTarget = "allowedVariables";
      this.showJsonDialog = true;
    },
    openBaitDataDialog() {
      this.jsonDialogTarget = "baitData";
      this.showJsonDialog = true;
    },
    openRedisLocksDialog() {
      this.jsonDialogTarget = "redisLocks";
      this.showJsonDialog = true;
    },
    openStimulusGroupDialog() {
      this.jsonDialogTarget = "stimulusGroup";
      this.showJsonDialog = true;
    },
    onJsonApply(value) {
      if (this.jsonDialogTarget === "baitData") {
        this.form.bait_data_text = this.stringifyJson(value);
        return;
      }
      if (this.jsonDialogTarget === "redisLocks") {
        this.form.redis_locks_text = this.stringifyJson(value);
        return;
      }
      if (this.jsonDialogTarget === "stimulusGroup") {
        this.form.stimulus_group_text = this.stringifyJson(value);
        return;
      }
      this.form.allowed_variables = value;
    },
    onJsonCancel() {
      this.showJsonDialog = false;
    },
    buildPayload() {
      const payload = {
        ...this.form,
        name: this.form.name.trim(),
        active: Boolean(this.form.active),
        bait_data: this.parseJsonField(this.form.bait_data_text, "Bait data"),
        allow_express: Boolean(this.form.allow_express),
        default_whatsapp_gateway_id: this.form.default_whatsapp_gateway_id,
        next_day: this.form.next_day || null,
        redis_locks:
          this.parseJsonField(this.form.redis_locks_text, "Redis locks") || {},
        stimulus_group:
          this.parseJsonField(
            this.form.stimulus_group_text,
            "Grupos de estimulo",
          ) || null,
        cons_schema: (this.form.cons_schema || "").trim(),
        whatsapp_quarentine_rules: this.form.whatsapp_quarentine_rules || DEFAULT_WHATSAPP_QUARENTINE_RULES,
      };
      delete payload.stimulus_group_text;
      delete payload.bait_data_text;
      delete payload.redis_locks_text;
      delete payload.default_sms_gateway;
      delete payload.default_email_gateway;
      delete payload.default_call_gateway;
      delete payload.sms_gateway_pool;
      if (!payload.bait_data) delete payload.bait_data;
      if (!payload.stimulus_group) delete payload.stimulus_group;
      if (!payload.last_importation) delete payload.last_importation;
      if (!payload.last_prepared_at) delete payload.last_prepared_at;

      return payload;
    },
    async saveCreditor() {
      this.isLoading = true;
      try {
        const payload = this.buildPayload();
        if (this.isEditMode) {
          await creditorService.update(this.creditorId, payload);
          this.snackbarSuccess("Credor atualizado com sucesso");
          this.resetForm();
        } else {
          const res = await creditorService.create(payload);
          this.snackbarSuccess("Credor criado com sucesso");
          this.$router.push({
            name: "Informações Dos Credores",
          });
        }
      } catch (error) {
        console.error(error);
        this.snackbarError(error.message || "Erro ao salvar credor");
      } finally {
        this.isLoading = false;
      }
    },
    async resetForm() {
      if (this.isEditMode) {
        await this.loadCreditor();
        this.buildMainStateSnapshot();
        return;
      }
      this.form = buildInitialForm();
    },
    snackbarError(message = "") {
      if (!message) return;

      this.$store.commit("showSnackbar", {
        message,
        color: "error",
      });
    },
    snackbarSuccess(message = "") {
      if (!message) return;

      this.$store.commit("showSnackbar", {
        message,
        color: "success",
      });
    },
    updateLockHours(key, newValue) {
      const locks = JSON.parse(this.form.redis_locks_text || "{}");
      if (locks[key]) {
        const parsedValue = Number(newValue);
        const normalizedValue = Number.isFinite(parsedValue)
          ? Math.max(0, parsedValue)
          : 0;

        locks[key].lock_hours = normalizedValue;
        this.form.redis_locks_text = JSON.stringify(locks, null, 2);
      }
    },
    updateLockByCreditorId(key, newValue) {
      const locks = JSON.parse(this.form.redis_locks_text || "{}");
      if (locks[key]) {
        locks[key].lock_by_creditor_id = newValue;
        this.form.redis_locks_text = JSON.stringify(locks, null, 2);
      }
    },
    buildMainStateSnapshot() {
      this.mainStateSnapshot = JSON.stringify(this.form);
    },
    removeVariable(index) {
      this.form.allowed_variables.splice(index, 1);
    },
    formatNewVariable() {
      this.newVariable = this.newVariable
        .toUpperCase()
        .replace(/[^A-Z0-9_$]/g, "");

      if (this.newVariable && !this.newVariable.startsWith("$"))
        this.newVariable = "$" + this.newVariable;
    },
    addVariable() {
      this.newVariable = this.newVariable.trim();

      if (!this.newVariable) return;

      if (!this.newVariable.startsWith("$"))
        this.newVariable = "$" + this.newVariable;

      if (this.form.allowed_variables.includes(this.newVariable)) {
        this.newVariable = "";
        return;
      }

      this.form.allowed_variables.push(this.newVariable);
      this.newVariable = "";
    },
    async viewHistory(pagination = {}) {
      if (!this.creditorId || this.loadingCreditorHistory) return;

      this.showCreditorHistory = true;
      this.loadingCreditorHistory = true;
      this.creditorHistoryItems = [];
      try {
        const params = {
          creditor_id: this.creditorId,
          limit: pagination.rowsPerPage || 10,
          page: pagination.page || 1,
        };
        const res = await creditorService.getHistory(params);

        this.creditorHistoryItems = res.objects || [];
        this.totalCreditorHistoryItems = res.num_results || 0;
      } catch (e) {
        console.error(e);
        this.snackbarError("Erro ao carregar histórico. tente novamente.");
        this.showCreditorHistory = false;
      } finally {
        this.loadingCreditorHistory = false;
      }
    },
    openConfigDiff(payload = {}) {
      this.selectedCurrentConfigDetails = payload.currentConfigDetails || {};
      this.selectedPreviousConfigDetails = payload.previousConfigDetails || {};
      this.selectedHistoryCreatedAt = payload.createdAt || "";
      this.selectedHistoryUserName = payload.userName || "";
      this.showConfigDiff = true;
    },
    handleConfigDiffClose() {
      this.showConfigDiff = false;
      this.selectedCurrentConfigDetails = {};
      this.selectedPreviousConfigDetails = {};
      this.selectedHistoryCreatedAt = "";
      this.selectedHistoryUserName = "";
    },
    handleUpdateWhatsappQuarentineRules(updatedRules) {
      this.form.whatsapp_quarentine_rules = updatedRules || DEFAULT_WHATSAPP_QUARENTINE_RULES;
    },
  },
};
</script>

<style scoped>
.creditors-manager {
  height: 100%;
}

.creditors-manager__layout {
  height: 100%;
  overflow: hidden;
}

.creditors-manager__header {
  position: sticky;
  top: 0;
  z-index: 2;
}

.creditors-manager__body {
  min-height: 0;
  overflow: hidden;
}

.creditors-manager__body-scroll {
  height: 100%;
  overflow-y: auto;
}

.creditors-manager__footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
}
</style>
